import { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { ContractFunnelsResponse, PricingModeEnum } from "api/pricing";
import { fetchFunnels } from '../thunks/fetchFunnelsThunk';
import { PricingState } from "../pricingSlice";
import { createContractFunnelsFromFunnels, createFiltersFromFunnels, createFormulaFunnels, createGuaranteesReferential } from "../utils/funnelUtils";
import { fetchFunnelsByGuarantees } from "../thunks/fetchFunnelsByGuaranteesThunk";
import { fetchFunnelsByVariablesOrFees } from "../thunks/fetchFunnelsByVariableOrFeesThunk";
import { createGuaranteeLastResearchHash } from "../utils/createGuaranteeLastResearchHash";
import { recordTunnelFeeValues } from "../utils/customFees";

/**
 * Every comparator response (initial load and custom-global reloads) carries the mode and the
 * formula cards. A formula is an ordinary card (filters, fees, cart, edit mode) with all its
 * guarantees and no score: it joins contractFunnels after the comparable products. A response
 * without mode (back without fixed formulas) is the classic comparator.
 */
const applyFunnels = (state: PricingState, response: ContractFunnelsResponse, optionalGuaranteeIds?: string[]) => {
  const funnels = response.contractFunnels ?? [];
  const formulas = response.formulas ?? [];
  state.funnels = [...funnels, ...formulas];
  recordTunnelFeeValues(state.tunnelFeeValues, state.funnels);
  state.contractFunnels = [
    ...createContractFunnelsFromFunnels(funnels, optionalGuaranteeIds),
    ...createFormulaFunnels(formulas),
  ];
  state.mode = response.mode ?? PricingModeEnum.Classic;
  state.formulasContext = response.formulasContext ?? null;
};

export const addFetchFunnelsReducers = (builder: ActionReducerMapBuilder<PricingState>) => {
  builder
    .addCase(fetchFunnels.pending, (state) => {
      state.loading.pricingPageLoading = true;
      // Another file's formulas must not show while this one loads: back to the classic page.
      state.mode = PricingModeEnum.Classic;
      state.formulasContext = null;
    })
    .addCase(fetchFunnels.fulfilled, (state, action) => {
      state.updatedContractFunnels = [];
      state.tunnelFeeValues = {};
      state.riskId = action.payload.riskId;
      const funnels = action.payload.contractFunnels;
      // Nouvelle structure plate: guarantees et sidebar sont directement à la racine
      const guarantees = action.payload.guarantees;
      const sidebar = action.payload.sidebar;

      if (guarantees) {
        const globalGuarantees = createGuaranteesReferential(guarantees)
        state.globalGuaranteesReferential = globalGuarantees;
        state.globalGuarantees = {
          ...globalGuarantees,
          optional: []
        };

        state.guaranteeLastResearchHash = createGuaranteeLastResearchHash({
          branchCode: "CHO",
          guarantees: [
            ...globalGuarantees.mandatory.map(g => ({
              id: g.id,
              deductible: g.deductible?.value,
              ceiling: g.ceiling?.value,
            }))
          ],
        })

      }

      if (funnels) {
        if (sidebar) {
          state.filters = createFiltersFromFunnels(sidebar);
        }
        applyFunnels(state, action.payload);
      }
      state.loading.pricingPageLoading = false;
    })
    .addCase(fetchFunnels.rejected, (state) => {
      state.loading.pricingPageLoading = false;
    });
};

export const addFetchFunnelsByGuaranteesReducers = (builder: ActionReducerMapBuilder<PricingState>) => {
  builder
    .addCase(fetchFunnelsByGuarantees.pending, (state) => {
      state.loading.contractFunnelsLoading = true
    })
    .addCase(fetchFunnelsByGuarantees.fulfilled, (state, action) => {
      state.updatedContractFunnels = [];
      if (action.payload.contractFunnels) {
        applyFunnels(state, action.payload, state.globalGuarantees.optional.map(g => g.id));
      }
      state.loading.contractFunnelsLoading = false
    })
    .addCase(fetchFunnelsByGuarantees.rejected, (state) => {
      state.loading.contractFunnelsLoading = false
    })
};

export const addFetchFunnelsByVariableOrFeesReducers = (builder: ActionReducerMapBuilder<PricingState>) => {
  builder
    .addCase(fetchFunnelsByVariablesOrFees.pending, (state) => {
      state.loading.contractFunnelsPricesLoading = true
    })
    .addCase(fetchFunnelsByVariablesOrFees.fulfilled, (state, action) => {
      if (action.payload.contractFunnels) {
        applyFunnels(state, action.payload, state.globalGuarantees.optional.map(g => g.id));
      }
      state.loading.contractFunnelsPricesLoading = false
    })
    .addCase(fetchFunnelsByVariablesOrFees.rejected, (state) => {
      state.loading.contractFunnelsPricesLoading = false
    })
};
